import { Fragment, useEffect, useState, type JSX } from 'react'
import { Input, Checkbox, Label, ToggleGroup, ToggleGroupItem, Button } from '@ttab/elephant-ui'
import { ChevronDownIcon, ChevronUpIcon } from '@ttab/elephant-ui/icons'
import { useTranslation } from 'react-i18next'
import { DateRangePicker } from '@/components/DateRangePicker'
import type { FieldPath, StructuredSearchState, SearchFieldConfig } from './types'

interface StructuredModeProps {
  state: StructuredSearchState
  fields: SearchFieldConfig[]
  onChange: (state: StructuredSearchState) => void
}

export const StructuredMode = ({ state, fields, onChange }: StructuredModeProps): JSX.Element => {
  const { t } = useTranslation()
  const hasAdvancedOptions = state.fuzzy || state.boost > 1 || state.fieldExists.length > 0
  const [showMore, setShowMore] = useState(hasAdvancedOptions)
  // OpenSearch doesn't support fuzziness on phrase queries, so fuzzy is disabled in phrase mode.
  const fuzzyDisabled = state.matchType === 'phrase'
  const showFuzzyOptions = state.fuzzy && !fuzzyDisabled

  useEffect(() => {
    if (hasAdvancedOptions) {
      setShowMore(true)
    }
  }, [hasAdvancedOptions])

  function updateField(fieldPath: FieldPath, checked: boolean) {
    const next = checked
      ? [...state.selectedFields, fieldPath]
      : state.selectedFields.filter((f) => f !== fieldPath)
    onChange({ ...state, selectedFields: next })
  }

  function updateFieldExists(field: FieldPath, exists: boolean) {
    const current = state.fieldExists.find((fe) => fe.field === field)
    if (current && current.exists === exists) {
      onChange({ ...state, fieldExists: state.fieldExists.filter((fe) => fe.field !== field) })
      return
    }

    const filtered = state.fieldExists.filter((fe) => fe.field !== field)
    onChange({
      ...state,
      fieldExists: [...filtered, { field, exists }],
      // Requiring a field to be absent contradicts searching in it, so marking it
      // missing takes it out of the field list rather than leaving both set.
      selectedFields: exists
        ? state.selectedFields
        : state.selectedFields.filter((f) => f !== field)
    })
  }

  function getFieldExistsValue(field: FieldPath): 'exists' | 'missing' | undefined {
    const fe = state.fieldExists.find((f) => f.field === field)
    if (!fe) return undefined
    return fe.exists ? 'exists' : 'missing'
  }

  return (
    <div className='flex flex-col gap-4'>
      {/* Query — most used */}
      <div className='flex flex-col gap-1.5'>
        <Label className='text-xs font-medium text-muted-foreground'>
          {t('advancedSearch.query')}
        </Label>
        <Input
          autoFocus
          value={state.query}
          onChange={(e) => onChange({ ...state, query: e.target.value })}
          placeholder={t('advancedSearch.searchTerms')}
        />
      </div>

      {/* Search in fields */}
      <div className='flex flex-col gap-1.5'>
        <Label
          className='text-xs font-medium text-muted-foreground w-fit'
          title={t('advancedSearch.hints.searchIn')}
        >
          {t('advancedSearch.searchIn')}
        </Label>
        <div className='flex flex-wrap gap-3'>
          {fields.map((field) => {
            const isMissing = getFieldExistsValue(field.fieldPath) === 'missing'
            return (
              <label
                key={field.fieldPath}
                className={`flex items-center gap-1.5 text-sm ${isMissing ? 'opacity-50' : ''}`}
                title={isMissing ? t('advancedSearch.hints.searchInDisabledForMissing') : undefined}
              >
                <Checkbox
                  checked={state.selectedFields.includes(field.fieldPath)}
                  disabled={isMissing}
                  onCheckedChange={(checked) => updateField(field.fieldPath, checked === true)}
                />
                {(t as (k: string) => string)(field.labelKey)}
              </label>
            )
          })}
        </div>
      </div>

      {/* Date range */}
      <div className='flex flex-col gap-1.5'>
        <Label className='text-xs font-medium text-muted-foreground'>
          {t('advancedSearch.dateRange')}
        </Label>
        <DateRangePicker
          value={state.dateRange}
          onChange={(dateRange) => onChange({ ...state, dateRange })}
        />
      </div>

      {/* Match type + AND */}
      <div className='flex flex-col gap-1.5'>
        <Label className='text-xs font-medium text-muted-foreground'>
          {t('advancedSearch.matchType')}
        </Label>
        <ToggleGroup
          type='single'
          value={[state.matchType]}
          onValueChange={(values: string[]) => {
            const value = values[0]
            if (value === 'best_fields' || value === 'phrase') {
              onChange({ ...state, matchType: value })
            }
          }}
          className='justify-start'
        >
          <ToggleGroupItem
            value='best_fields'
            className='text-xs'
            title={t('advancedSearch.hints.words')}
          >
            {t('advancedSearch.words')}
          </ToggleGroupItem>
          <ToggleGroupItem
            value='phrase'
            className='text-xs'
            title={t('advancedSearch.hints.exactPhrase')}
          >
            {t('advancedSearch.exactPhrase')}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <label
        className='flex items-center gap-1.5 text-sm w-fit'
        title={t('advancedSearch.hints.requireAllTerms')}
      >
        <Checkbox
          checked={state.booleanAnd}
          onCheckedChange={(checked) => onChange({
            ...state, booleanAnd: checked === true
          })}
        />
        {t('advancedSearch.requireAllTerms')}
      </label>

      {/* More options toggle */}
      <Button
        variant='ghost'
        size='xs'
        onClick={() => setShowMore(!showMore)}
        className='flex items-center gap-1 px-0 h-6 text-xs text-muted-foreground justify-start'
      >
        {showMore ? <ChevronUpIcon size={14} /> : <ChevronDownIcon size={14} />}
        {t('advancedSearch.moreOptions')}
      </Button>

      {showMore && (
        <div className='flex flex-col gap-4'>
          {/* Fuzzy */}
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-3'>
              <label
                className={`flex items-center gap-1.5 text-sm w-fit ${fuzzyDisabled ? 'opacity-50' : ''}`}
                title={fuzzyDisabled
                  ? t('advancedSearch.hints.fuzzyDisabledForPhrase')
                  : t('advancedSearch.hints.fuzzyMatching')}
              >
                <Checkbox
                  checked={state.fuzzy}
                  disabled={fuzzyDisabled}
                  onCheckedChange={(checked) => onChange({
                    ...state, fuzzy: checked === true
                  })}
                />
                {t('advancedSearch.fuzzyMatching')}
              </label>
              {showFuzzyOptions && (
                <ToggleGroup
                  type='single'
                  value={[String(state.fuzzyEdits)]}
                  onValueChange={(values: string[]) => {
                    const value = values[0]
                    if (value === 'auto') {
                      onChange({ ...state, fuzzyEdits: 'auto' })
                    } else if (value === '1' || value === '2') {
                      onChange({ ...state, fuzzyEdits: Number(value) as 1 | 2 })
                    }
                  }}
                  className='justify-start'
                  title={t('advancedSearch.hints.fuzzyEdits')}
                >
                  <ToggleGroupItem value='auto' className='text-xs h-7 px-2'>
                    {t('advancedSearch.fuzzyAuto')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value='1' className='text-xs h-7 px-2'>
                    {t('advancedSearch.fuzzyEdit1')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value='2' className='text-xs h-7 px-2'>
                    {t('advancedSearch.fuzzyEdit2')}
                  </ToggleGroupItem>
                </ToggleGroup>
              )}
            </div>
            {showFuzzyOptions && (
              <div className='flex items-center gap-2 ml-6'>
                <Label
                  className='text-xs text-muted-foreground'
                  title={t('advancedSearch.hints.prefixLength')}
                >
                  {t('advancedSearch.prefixLength')}
                </Label>
                <Input
                  type='number'
                  min={0}
                  max={5}
                  value={state.fuzzyPrefixLength}
                  onChange={(e) => onChange({ ...state, fuzzyPrefixLength: Math.max(0, Math.min(5, Number(e.target.value) || 0)) })}
                  className='w-16 h-7 text-xs'
                />
              </div>
            )}
          </div>

          {/* Boost */}
          <div className='flex items-center gap-2'>
            <Label
              className='text-xs text-muted-foreground'
              title={t('advancedSearch.hints.boost')}
            >
              {t('advancedSearch.boost')}
            </Label>
            <Input
              type='number'
              min={1}
              max={10}
              step={0.5}
              value={state.boost}
              onChange={(e) => onChange({ ...state, boost: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })}
              className='w-20 h-7 text-xs'
            />
          </div>

          {/* Field existence */}
          <div className='flex flex-col gap-1.5'>
            <Label
              className='text-xs font-medium text-muted-foreground w-fit'
              title={t('advancedSearch.hints.fieldExists')}
            >
              {t('advancedSearch.fieldExists')}
            </Label>
            <div className='grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 items-center'>
              {fields.map((field) => {
                const value = getFieldExistsValue(field.fieldPath)
                return (
                  <Fragment key={field.fieldPath}>
                    <span className='text-xs font-medium'>
                      {(t as (k: string) => string)(field.labelKey)}
                    </span>
                    <ToggleGroup
                      type='single'
                      value={value ? [value] : []}
                      onValueChange={(values: string[]) => {
                        const v = values[0]
                        if (v === 'exists' || v === 'missing') {
                          updateFieldExists(
                            field.fieldPath, v === 'exists'
                          )
                        } else {
                          onChange({
                            ...state,
                            fieldExists: state.fieldExists.filter(
                              (fe) => fe.field !== field.fieldPath
                            )
                          })
                        }
                      }}
                      className='justify-start'
                    >
                      <ToggleGroupItem
                        value='exists'
                        className='text-[10px] h-6 px-1.5'
                      >
                        {t('advancedSearch.exists')}
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value='missing'
                        className='text-[10px] h-6 px-1.5'
                      >
                        {t('advancedSearch.missing')}
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </Fragment>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
